/**
 * Taurine scroll timeline.
 *
 * Progress is a pure function of scroll position, not a timed animation:
 * scrolling down advances it, stopping holds it, scrolling up reverses it.
 *
 * The DOM is never rebuilt. Each frame writes two custom properties:
 *   --trt-p   on the track      (0..1) -> height of the vertical fill
 *   --trt-ms  on each milestone (0..1) -> width of that bar's fill
 *
 * Scoped to <tr-timeline>, so several instances coexist and Theme Editor
 * section reloads re-initialise cleanly.
 */
const TRT_REDUCED = '(prefers-reduced-motion: reduce)';

class TrTimeline extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-trt-track]');
    if (!this.track) return;

    this.milestones = Array.from(this.querySelectorAll('[data-trt-milestone]'));
    this.rail = this.querySelector('.tr-timeline__rail');
    this.dots = Array.from(this.querySelectorAll('.tr-timeline__dot'));

    this.#measureRail();
    // Header heights change when titles wrap or fonts swap, which moves the
    // dot centres, so re-measure on any reflow of the track rather than only
    // on window resize.
    if ('ResizeObserver' in window) {
      this.railObserver = new ResizeObserver(() => this.#measureRail());
      this.railObserver.observe(this.track);
    }

    // With animation disabled, present the finished state rather than an
    // empty one — the content must still read correctly.
    if (!this.hasAttribute('data-trt-animate')) {
      this.#paint(1, () => 1);
      return;
    }

    this.readLine = Number(this.dataset.trtReadline) || 0.6;
    this.ticking = false;
    this.visible = false;

    this.onScroll = () => this.#schedule();
    this.onResize = () => this.#schedule();

    // Only listen while the section is on screen: no scroll work is done for
    // a timeline far up or down the page.
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.visible = entry.isIntersecting;
          if (this.visible) {
            this.#bind();
            this.#schedule();
          } else {
            this.#unbind();
          }
        });
      },
      { rootMargin: '200px 0px' }
    );
    this.observer.observe(this);

    this.#update();
  }

  #bind() {
    if (this.bound) return;
    this.bound = true;
    // Capture on document, not a scroll listener on window.
    //
    // This theme does not always scroll the window: at desktop widths html and
    // body are overflow-y: hidden and `.page-wrapper` is the scroll container,
    // while on mobile the document scrolls normally. Scroll events do not
    // bubble, so a window listener receives nothing in the desktop case and the
    // timeline never advances.
    //
    // A capture-phase listener on document sees scroll from any scroller,
    // whichever one the theme happens to use at this breakpoint.
    //
    // Passive: these handlers only read geometry and must never block the
    // compositor during a scroll.
    document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
  }

  #unbind() {
    if (!this.bound) return;
    this.bound = false;
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onResize);
  }

  /**
   * Pin the rail between the centre of the first dot and the centre of the
   * last, so the line begins and ends on a dot instead of running past both.
   */
  #measureRail() {
    if (!this.rail || this.dots.length === 0) return;

    const trackTop = this.track.getBoundingClientRect().top;
    const centre = (dot) => {
      const r = dot.getBoundingClientRect();
      return r.top + r.height / 2 - trackTop;
    };

    const first = centre(this.dots[0]);
    const last = centre(this.dots[this.dots.length - 1]);

    this.track.style.setProperty('--trt-rail-top', `${first}px`);
    this.track.style.setProperty('--trt-rail-height', `${Math.max(0, last - first)}px`);
  }

  /** Coalesce bursts of scroll events into one write per frame. */
  #schedule() {
    if (this.ticking) return;
    this.ticking = true;
    this.frame = requestAnimationFrame(() => {
      this.ticking = false;
      this.#update();
    });
  }

  #update() {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    // The notional line across the viewport that progress is measured against.
    const line = viewportHeight * this.readLine;

    const rect = this.track.getBoundingClientRect();
    const height = rect.height || 1;
    const overall = TrTimeline.#clamp((line - rect.top) / height);

    this.#paint(overall, (milestone) => {
      const r = milestone.getBoundingClientRect();
      return TrTimeline.#clamp((line - r.top) / (r.height || 1));
    });
  }

  #paint(overall, progressFor) {
    this.track.style.setProperty('--trt-p', overall.toFixed(4));

    this.milestones.forEach((milestone) => {
      const p = progressFor(milestone);
      milestone.style.setProperty('--trt-ms', p.toFixed(4));
      // A milestone counts as active the moment its fill starts, and stays
      // complete once filled, so the states never flicker back.
      milestone.classList.toggle('is-active', p > 0);
      milestone.classList.toggle('is-complete', p >= 1);
    });
  }

  static #clamp(value) {
    if (Number.isNaN(value)) return 0;
    return Math.min(1, Math.max(0, value));
  }

  disconnectedCallback() {
    this.#unbind();
    this.observer?.disconnect();
    this.railObserver?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }
}

if (!customElements.get('tr-timeline')) customElements.define('tr-timeline', TrTimeline);
